import { useEffect, useMemo, useRef } from 'react';
import { recordToArray } from '@utils/recordToArray';
import { useAppDispatch, useAppSelector } from '@app/store';
import { LotteryFinishedState } from '@features/LotteryGame/types';
import {
  lotteryGameFinished,
  selectLotteryLotterySessions,
} from '@features/LotteryGame/store';
import { useLazyHistoryControllerGetLotteryGamesFinishedStatesQuery } from '@app/store/api/historyApi';

export const useReconnectedGames = (refetchUser: VoidFunction) => {
  const dispatch = useAppDispatch();
  const connected = useAppSelector((s) => s.game.connected);
  const gamesRecord = useAppSelector(selectLotteryLotterySessions);
  const firstConnectionRef = useRef(true);

  const {
    reconnectedGamesIds,
    unreleasedGameIds,
  }: { reconnectedGamesIds: string[]; unreleasedGameIds: string[] } = useMemo(() => {
    const games = recordToArray(gamesRecord);
    const reconnectedGamesIds = games
      .filter((g) => g.lostConnection && g.gameStarted && !g.finishState)
      .map((g) => g.id);

    const unreleasedGameIds = games
      .filter((g) => g.lostConnection && !g.gameStarted)
      .map((g) => g.id);

    return { reconnectedGamesIds, unreleasedGameIds };
  }, [gamesRecord]);

  const [trigger] = useLazyHistoryControllerGetLotteryGamesFinishedStatesQuery();

  const getGamesState = async () => {
    if (reconnectedGamesIds.length === 0) return;

    try {
      const payload = await trigger({ gameRoomIds: reconnectedGamesIds }).unwrap();

      payload.records.forEach((game) => {
        dispatch(lotteryGameFinished(game as LotteryFinishedState));
      });
    } catch (error) {
      console.error('reconnected games error', error);
    }
  };

  useEffect(() => {
    if (!connected) return;

    const firstConnection = firstConnectionRef.current;

    if (connected && firstConnection) firstConnectionRef.current = false;

    if (reconnectedGamesIds.length > 0 && connected && !firstConnection) {
      getGamesState();
    }
    if (unreleasedGameIds.length > 0) refetchUser();

    const timer = setTimeout(() => {
      getGamesState();
    }, 30000);

    return () => clearTimeout(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  return null;
};
